"use client"

import * as React from "react"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useControllableState } from "@radix-ui/react-use-controllable-state"
import {
  AnimatePresence,
  motion,
  Transition
} from "framer-motion"

import { cn } from "../lib/utils"

const DialogContext = React.createContext<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null)

const useDialog = () => {
  const context = React.useContext(DialogContext)
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider")
  }
  return context
}

function Dialog({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen ?? false,
    onChange: onOpenChange,
    caller: "Dialog",
  })

  const contextValue = React.useMemo(
    () => ({
      open,
      setOpen,
    }),
    [open, setOpen]
  )

  return (
    <DialogContext.Provider value={contextValue}>
      <DialogPrimitive.Root
        data-slot="dialog"
        open={open}
        onOpenChange={setOpen}
        {...props}
      />
    </DialogContext.Provider>
  )
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger
    data-slot="dialog-trigger"
    {...props}
  />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal
    data-slot="dialog-portal"
    {...props}
  />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close
    data-slot="dialog-close"
    {...props}
  />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-black/80  data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      data-slot="dialog-overlay"
      {...props}
    />
  )
}

type FlipDirection = "top" | "bottom" | "left" | "right"

function DialogContent({
  className,
  children,
  from = "left",
  transition = { type: "spring", stiffness: 150, damping: 25 },
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  from?: FlipDirection;
  transition?: Transition;
}) {
  const { open } = useDialog()

  const initialRotation =
    from === "top" || from === "left" ? "20deg" : "-20deg"
  const isVertical = from === "top" || from === "bottom"
  const rotateAxis = isVertical ? "rotateX" : "rotateY"

  return (
    <AnimatePresence>
      {open && (
        <DialogPortal forceMount>
          <DialogOverlay
            asChild
            forceMount
          >
            <motion.div
              key="dialog-overlay"
              animate={{ opacity: 1, backdropFilter: "blur(4px)" }}
              exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
              initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            />
          </DialogOverlay>
          <DialogPrimitive.Content
            asChild
            forceMount
            {...props}
          >
            <motion.div
              key="dialog-content"
              animate={{
                opacity: 1,
                filter: "blur(0px)",
                transform: `perspective(500px) ${rotateAxis}(0deg) scale(1)`,
              }}
              className={cn(
                "fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-2rem)] max-w-lg gap-4 bg-neutral-25 p-6 shadow-lg rounded-xl",
                className
              )}
              data-slot="dialog-content"
              exit={{
                opacity: 0,
                filter: "blur(4px)",
                transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`,
              }}
              initial={{
                opacity: 0,
                filter: "blur(4px)",
                transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`,
              }}
              style={{
                translate: "-50% -50%",
              }}
              transition={transition}
            >
              {children}
              {/* {isClosable && (
                <DialogPrimitive.Close
                  className={cn(
                    "ring-offset-neutral-25 focus:ring-neutral-200 data-[state=open]:bg-neutral-50 absolute top-7 right-6 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6",
                    classNameClose
                  )}
                >
                  <XIcon />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              )} */}
            </motion.div>
          </DialogPrimitive.Content>
        </DialogPortal>
      )}
    </AnimatePresence>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        className
      )}
      data-slot="dialog-header"
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className
      )}
      data-slot="dialog-footer"
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        className
      )}
      data-slot="dialog-title"
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-sm text-muted-foreground", className)}
      data-slot="dialog-description"
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
